import { escapeHtmlAttribute } from "./icon-html"

export interface TooltipAttributes {
	tip: string
	headline: string | null
	cta: string | null
	href: string | null
}

const text = (value: unknown): string | null => {
	if (typeof value !== "string") return null
	const trimmed = value.trim()
	return trimmed ? trimmed : null
}

// Only links a reader can follow safely: web, mail, or a path on the site.
export function sanitizeTooltipHref(value: unknown): string | null {
	const href = text(value)
	if (!href) return null
	if (/^(?:https?:|mailto:)/i.test(href)) return href
	if (/^[^:]*$/.test(href)) return href
	return null
}

export function normalizeTooltipAttributes(
	raw: Partial<Record<keyof TooltipAttributes, unknown>>,
): TooltipAttributes {
	const href = sanitizeTooltipHref(raw.href)
	return {
		tip: text(raw.tip) ?? "",
		headline: text(raw.headline),
		// Mintlify needs both for the link, so one without the other is dropped.
		cta: href ? text(raw.cta) : null,
		href: text(raw.cta) ? href : null,
	}
}

export function tooltipElementAttributes(
	attributes: TooltipAttributes,
): Record<string, string> {
	return {
		"data-type": "tooltip",
		"data-tip": attributes.tip,
		...(attributes.headline ? { "data-headline": attributes.headline } : {}),
		...(attributes.cta ? { "data-cta": attributes.cta } : {}),
		...(attributes.href ? { "data-href": attributes.href } : {}),
		class: "pl-tooltip",
	}
}

type Spec = [string, Record<string, string>, ...(Spec | string)[]]

/** The popup, as a DOM spec the mark renders beside its content. */
export function tooltipPopupSpec(attributes: TooltipAttributes): Spec {
	const parts: Spec[] = []
	if (attributes.headline) {
		parts.push(["span", { class: "pl-tooltip-headline" }, attributes.headline])
	}
	if (attributes.tip) {
		parts.push(["span", { class: "pl-tooltip-tip" }, attributes.tip])
	}
	if (attributes.cta && attributes.href) {
		parts.push([
			"a",
			{ class: "pl-tooltip-cta", href: attributes.href },
			attributes.cta,
		])
	}
	return [
		"span",
		{ class: "pl-tooltip-content", role: "tooltip", contenteditable: "false" },
		["span", { class: "pl-tooltip-box" }, ...parts],
	]
}

const escapeText = (value: string): string =>
	value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		// Markdown would read these in the importer's raw HTML as emphasis or links.
		.replace(/\*/g, "&#42;")
		.replace(/_/g, "&#95;")
		.replace(/`/g, "&#96;")
		.replace(/\[/g, "&#91;")
		.replace(/\]/g, "&#93;")

function specToHtml(spec: Spec | string): string {
	if (typeof spec === "string") return escapeText(spec)
	const [tag, attrs, ...children] = spec
	const serialized = Object.entries(attrs)
		.map(([key, value]) => ` ${key}="${escapeHtmlAttribute(value)}"`)
		.join("")
	return `<${tag}${serialized}>${children.map(specToHtml).join("")}</${tag}>`
}

export function tooltipHtml(
	raw: Partial<Record<keyof TooltipAttributes, unknown>>,
	triggerHtml: string,
): string {
	const attributes = normalizeTooltipAttributes(raw)
	const outer = Object.entries(tooltipElementAttributes(attributes))
		.map(([key, value]) => ` ${key}="${escapeHtmlAttribute(value)}"`)
		.join("")
	return `<span${outer}><span class="pl-tooltip-trigger">${triggerHtml}</span>${specToHtml(
		tooltipPopupSpec(attributes),
	)}</span>`
}
